/**
 * DocumentQRCode Component
 *
 * Displays a QR code for document verification on printed documents.
 * The QR code contains a verification URL that can be scanned to verify
 * the document's authenticity.
 *
 * Usage:
 *   <DocumentQRCode
 *     verificationCode="ABC123XYZ"
 *     documentType="Job Card"
 *     documentNumber="JC000001"
 *   />
 */

import { Box, Typography, CircularProgress } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'

const DocumentQRCode = ({
  verificationCode,
  documentType,
  documentNumber,
  size = 80,
  showLabel = true,
  showCode = true,
}) => {
  const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin
  const verificationUrl = verificationCode
    ? `${baseUrl}/verify/${verificationCode}`
    : null

  return (
    <Box
      className="document-qr-code"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 1,
        border: '1px solid #ccc',
        borderRadius: 1,
        backgroundColor: 'white',
        minWidth: size + 16,
        minHeight: size + 40,
        '@media print': {
          border: '1px solid #000',
          pageBreakInside: 'avoid',
        },
      }}
    >
      {verificationUrl ? (
        <QRCodeSVG
          value={verificationUrl}
          size={size}
          level="M"
          includeMargin={false}
        />
      ) : (
        <Box
          sx={{
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f5f5f5',
            borderRadius: 0.5,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {showLabel && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            fontSize: '7px',
            fontWeight: 'bold',
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Scan to Verify
        </Typography>
      )}
      {showCode && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '6px',
            color: 'text.secondary',
            textAlign: 'center',
            fontFamily: 'monospace',
          }}
        >
          {verificationCode || 'Loading...'}
        </Typography>
      )}
    </Box>
  )
}

export default DocumentQRCode
